import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Inbox, Bug, HelpCircle, Lightbulb, MoreHorizontal, CheckCircle2, RotateCw, Ban, Clock, ChevronRight, Mail, Send, Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

const STATUSES = [
  { key: "all",         label: "All" },
  { key: "open",        label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved",    label: "Resolved" },
  { key: "spam",        label: "Spam" },
];

const STATUS_THEME = {
  open:        "bg-amber-100 text-amber-900",
  in_progress: "bg-blue-100 text-blue-900",
  resolved:    "bg-emerald-100 text-emerald-900",
  spam:        "bg-stone-200 text-stone-700",
};

const KIND_META = {
  bug:      { icon: Bug,         label: "Bug",      className: "text-red-700 bg-red-50 border-red-200" },
  question: { icon: HelpCircle,  label: "Question", className: "text-blue-700 bg-blue-50 border-blue-200" },
  feature:  { icon: Lightbulb,   label: "Feature",  className: "text-amber-700 bg-amber-50 border-amber-200" },
  other:    { icon: MoreHorizontal, label: "Other", className: "text-stone-700 bg-stone-100 border-stone-200" },
};

const STATUS_ACTIONS = [
  { key: "resolved",    label: "Mark resolved", Icon: CheckCircle2 },
  { key: "in_progress", label: "In progress",   Icon: Clock },
  { key: "open",        label: "Reopen",        Icon: RotateCw },
  { key: "spam",        label: "Spam",          Icon: Ban },
];

export default function AdminSupportInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [openItem, setOpenItem] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Always fetch ALL so we can compute per-status counts client-side, then filter.
      const r = await api.get("/admin/support", { params: { limit: 500 } });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load support inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: items.length, open: 0, in_progress: 0, resolved: 0, spam: 0 };
    items.forEach((it) => { c[it.status] = (c[it.status] || 0) + 1; });
    return c;
  }, [items]);

  // Delivery analytics across all visible items — sent/failed counts and
  // a "retry" pile = requests with a trailing failed admin reply that
  // hasn't been followed up by a successful one.
  const deliveryStats = useMemo(() => {
    let sent = 0, failed = 0, retry = 0;
    items.forEach((it) => {
      const adminReplies = (it.replies || []).filter((r) => (r.from || "admin") === "admin");
      if (adminReplies.length === 0) return;
      adminReplies.forEach((r) => {
        if (r.sent_status === "failed") failed += 1;
        else if (r.sent_status === "sent" || !r.sent_status) sent += 1;
      });
      // "Pending retry" = the most recent admin reply failed and no later success.
      const last = adminReplies[adminReplies.length - 1];
      if (last && last.sent_status === "failed") retry += 1;
    });
    return { sent, failed, retry };
  }, [items]);

  const filtered = useMemo(
    () => (statusFilter === "all" ? items : items.filter((it) => it.status === statusFilter)),
    [items, statusFilter],
  );

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/support/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      if (openItem?.id === id) setOpenItem({ ...openItem, status });
      // Tell the sidebar badge to recount immediately.
      window.dispatchEvent(new CustomEvent("support:read"));
      toast.success(`Marked as ${status.replace("_", " ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  // Collapse to a compact strip when inbox is at zero and the user hasn't
  // expanded manually. Keeps the admin page tight on quiet days.
  const isQuiet = counts.open === 0 && counts.all === 0;
  if (isQuiet && !expanded && !loading) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="w-full bg-white border border-stone-200 rounded-2xl px-5 py-3 flex items-center gap-3 hover:border-stone-300 transition-colors text-left"
        data-testid="support-inbox-collapsed"
      >
        <Inbox className="w-4 h-4 text-stone-400" />
        <span className="text-sm font-medium text-stone-800">Support inbox</span>
        <span className="text-[10px] uppercase tracking-[0.16em] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
          Inbox zero
        </span>
        <span className="text-xs text-stone-500 ml-auto">{counts.resolved + counts.spam} archived</span>
        <ChevronRight className="w-4 h-4 text-stone-400" />
      </button>
    );
  }
  if (counts.open === 0 && counts.all > 0 && !expanded && !loading) {
    // Some history exists but nothing open — slim "0 open · N archived" strip.
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="w-full bg-white border border-stone-200 rounded-2xl px-5 py-3 flex items-center gap-3 hover:border-stone-300 transition-colors text-left"
        data-testid="support-inbox-collapsed"
      >
        <Inbox className="w-4 h-4 text-stone-400" />
        <span className="text-sm font-medium text-stone-800">Support inbox</span>
        <span className="text-[10px] uppercase tracking-[0.16em] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full" data-testid="zero-open-badge">
          0 open
        </span>
        <span className="text-xs text-stone-500 ml-auto">
          {counts.all} total · {counts.resolved} resolved
        </span>
        <ChevronRight className="w-4 h-4 text-stone-400" />
      </button>
    );
  }

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="support-inbox-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4 text-stone-500" />
            <h2 className="font-display text-xl font-medium">Support inbox</h2>
            {counts.open > 0 && (
              <span className="text-[10px] uppercase tracking-[0.16em] font-semibold bg-amber-500 text-amber-50 px-2 py-0.5 rounded-full" data-testid="open-count-badge">
                {counts.open} open
              </span>
            )}
          </div>
          <p className="text-xs text-stone-500 mt-1">Inbound requests from the landing footer.</p>
        </div>
        {(deliveryStats.sent || deliveryStats.failed) > 0 && (
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-semibold" data-testid="delivery-analytics">
            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full" title="Replies successfully delivered" data-testid="stat-sent">
              <CheckCircle2 className="w-3 h-3" /> {deliveryStats.sent} sent
            </span>
            <span className="inline-flex items-center gap-1 bg-red-50 text-red-800 border border-red-200 px-2 py-0.5 rounded-full" title="Total failed deliveries" data-testid="stat-failed">
              <AlertTriangle className="w-3 h-3" /> {deliveryStats.failed} failed
            </span>
            {deliveryStats.retry > 0 && (
              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 rounded-full" title="Threads whose most-recent reply still needs to be retried" data-testid="stat-retry">
                <RotateCw className="w-3 h-3" /> {deliveryStats.retry} pending retry
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="status-filter-chips">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setStatusFilter(s.key)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              statusFilter === s.key
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
            }`}
            data-testid={`filter-${s.key}`}
          >
            {s.label} <span className="opacity-70 ml-1">{counts[s.key] || 0}</span>
          </button>
        ))}
      </div>

      {loading && filtered.length === 0 && (
        <div className="text-sm text-stone-500 py-6 text-center" data-testid="support-loading">Loading…</div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-sm text-stone-500 py-6 text-center" data-testid="support-empty">
          {statusFilter === "open" ? "Inbox zero. Nice." : "Nothing here yet."}
        </div>
      )}

      <ul className="divide-y divide-stone-100" data-testid="support-list">
        {filtered.map((it) => (
          <SupportRow key={it.id} item={it} onOpen={() => setOpenItem(it)} onResolve={() => updateStatus(it.id, "resolved")} />
        ))}
      </ul>

      <SupportDetail
        item={openItem}
        onClose={() => setOpenItem(null)}
        onChangeStatus={(s) => updateStatus(openItem.id, s)}
        onReplied={(updated) => {
          setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
          setOpenItem(updated);
        }}
      />
    </div>
  );
}

function SupportRow({ item, onOpen, onResolve }) {
  const kind = KIND_META[item.kind] || KIND_META.other;
  const KindIcon = kind.icon;
  const isResolved = item.status === "resolved";
  return (
    <li className="py-3 flex items-center gap-3" data-testid={`support-row-${item.id}`}>
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-1 rounded-md border ${kind.className}`} title={kind.label}>
        <KindIcon className="w-3 h-3" />
      </span>
      <button
        type="button"
        onClick={onOpen}
        className="flex-1 min-w-0 text-left group"
        data-testid={`support-open-${item.id}`}
      >
        <div className="flex items-center gap-2">
          <span className={`font-medium truncate ${isResolved ? "text-stone-500 line-through" : "text-stone-900"}`}>{item.subject}</span>
          <ChevronRight className="w-3.5 h-3.5 text-stone-300 group-hover:text-stone-600 transition-colors shrink-0" />
        </div>
        <div className="text-xs text-stone-500 truncate mt-0.5">
          <span className="font-medium text-stone-700">{item.name}</span> · {item.email} · {format(parseISO(item.created_at), "MMM d, h:mm a")}
        </div>
      </button>
      <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full ${STATUS_THEME[item.status] || STATUS_THEME.open}`} data-testid={`support-status-${item.id}`}>
        {item.status.replace("_", " ")}
      </span>
      {!isResolved && (
        <Button
          size="sm"
          variant="outline"
          className="rounded-full text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50 h-7"
          onClick={(e) => { e.stopPropagation(); onResolve(); }}
          data-testid={`support-resolve-${item.id}`}
          title="Mark resolved"
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve
        </Button>
      )}
    </li>
  );
}

function SupportDetail({ item, onClose, onChangeStatus, onReplied }) {
  const [composerOpen, setComposerOpen] = useState(false);
  const [retrying, setRetrying] = useState(null);
  if (!item) return null;
  const kind = KIND_META[item.kind] || KIND_META.other;
  const KindIcon = kind.icon;

  const retry = async (reply) => {
    setRetrying(reply.id);
    try {
      const r = await api.post(`/admin/support/${item.id}/reply`, {
        body: reply.body,
        mark_resolved: false,
      });
      if (r.data.ok) {
        toast.success("Reply re-sent");
      } else {
        toast.error("Still failed: " + (r.data.reply?.error || "email provider rejected"));
      }
      onReplied?.(r.data.request);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="support-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-1 rounded-md border ${kind.className}`}>
              <KindIcon className="w-3 h-3" /> {kind.label}
            </span>
            <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full ${STATUS_THEME[item.status] || STATUS_THEME.open}`}>
              {item.status.replace("_", " ")}
            </span>
          </div>
          <DialogTitle className="font-display text-xl mt-2 font-medium leading-snug">{item.subject}</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            From <span className="font-medium text-stone-700">{item.name}</span> ·
            <span className="ml-1 text-stone-600">{item.email}</span>
            · {format(parseISO(item.created_at), "MMM d, yyyy · h:mm a")}
          </DialogDescription>
        </DialogHeader>
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 max-h-[40vh] overflow-y-auto text-sm whitespace-pre-wrap text-stone-800 font-mono leading-relaxed" data-testid="support-detail-message">
          {item.message}
        </div>
        {item.user_agent && (
          <div className="text-[11px] text-stone-400 truncate" title={item.user_agent}>UA: {item.user_agent}</div>
        )}
        {(item.replies || []).length > 0 && (
          <div className="mt-3 space-y-2" data-testid="support-detail-replies">
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">Conversation</div>
            {item.replies.map((r) => {
              const isUser = r.from === "user";
              if (isUser) {
                return (
                  <div
                    key={r.id}
                    className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs ml-6"
                    data-testid={`reply-${r.id}`}
                  >
                    <div className="text-[11px] text-blue-900 mb-1 font-bold uppercase tracking-[0.14em]">From {item.name}</div>
                    <div className="text-[11px] text-stone-500 mb-1">{format(parseISO(r.sent_at), "MMM d, h:mm a")}</div>
                    <div className="whitespace-pre-wrap text-stone-800">{r.body}</div>
                  </div>
                );
              }
              const failed = r.sent_status === "failed";
              return (
                <div
                  key={r.id}
                  className={`border rounded-lg p-3 text-xs ${failed ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}
                  data-testid={`reply-${r.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-[11px] text-stone-500">
                      By <span className="font-medium text-stone-700">{r.admin_email}</span> · {format(parseISO(r.sent_at), "MMM d, h:mm a")}
                    </div>
                    {failed ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-red-800 bg-red-100 border border-red-200 px-2 py-0.5 rounded-full" data-testid={`reply-status-failed-${r.id}`}>
                        <AlertTriangle className="w-3 h-3" /> Failed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`reply-status-sent-${r.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Sent
                      </span>
                    )}
                  </div>
                  <div className="whitespace-pre-wrap text-stone-800">{r.body}</div>
                  {failed && (
                    <>
                      {r.error && <div className="mt-2 text-[11px] text-red-700 font-mono">{r.error}</div>}
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full mt-2 border-red-300 text-red-800 hover:bg-red-100 h-7 text-xs"
                        onClick={() => retry(r)}
                        disabled={retrying === r.id}
                        data-testid={`reply-retry-${r.id}`}
                      >
                        {retrying === r.id ? (<><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Retrying…</>) : (<><RotateCw className="w-3 h-3 mr-1.5" /> Retry send</>)}
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            size="sm"
            className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
            onClick={() => setComposerOpen(true)}
            data-testid="support-detail-reply-compose"
          >
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Reply
          </Button>
          <span className="flex-1" />
          {STATUS_ACTIONS.filter((a) => a.key !== item.status).map((a) => (
            <Button
              key={a.key}
              size="sm"
              variant={a.key === "resolved" ? "default" : "outline"}
              className={`rounded-full ${a.key === "resolved" ? "bg-emerald-500 hover:bg-emerald-400 text-white" : ""}`}
              onClick={() => onChangeStatus(a.key)}
              data-testid={`support-detail-${a.key}`}
            >
              <a.Icon className="w-3.5 h-3.5 mr-1.5" /> {a.label}
            </Button>
          ))}
        </div>
        <ReplyComposer
          item={item}
          open={composerOpen}
          onClose={() => setComposerOpen(false)}
          onReplied={(updated) => { setComposerOpen(false); onReplied?.(updated); }}
        />
      </DialogContent>
    </Dialog>
  );
}

function ReplyComposer({ item, open, onClose, onReplied }) {
  const [body, setBody] = useState("");
  const [markResolved, setMarkResolved] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setBody("");
      setMarkResolved(true);
    }
  }, [open]);

  const send = async () => {
    if (body.trim().length < 2) {
      toast.error("Reply is too short");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/admin/support/${item.id}/reply`, { body, mark_resolved: markResolved });
      if (r.data.ok) {
        toast.success("Reply sent");
      } else {
        // Persisted with sent_status=failed — let the admin retry from the detail dialog.
        toast.error("Email failed to send — saved as failed; retry from the thread.");
      }
      // Tell the sidebar badge to recount immediately (reply auto-touches status).
      window.dispatchEvent(new CustomEvent("support:read"));
      onReplied?.(r.data.request);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="support-reply-composer">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">Reply to {item.name}</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Sending to <span className="font-medium text-stone-700">{item.email}</span> · Subject:
            <span className="font-medium text-stone-700"> Re: {item.subject}</span>
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={8}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={`Hi ${item.name?.split(" ")[0] || "there"},\n\nThanks for reaching out…`}
          className="text-sm"
          data-testid="support-reply-body"
        />
        <div className="flex items-center justify-between gap-3 pt-1">
          <label className="flex items-center gap-2 text-xs text-stone-700">
            <Switch checked={markResolved} onCheckedChange={setMarkResolved} data-testid="support-reply-resolve-switch" />
            Mark request as resolved
          </label>
          <span className="text-[11px] text-stone-400">{body.length}/8000</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" disabled={sending} data-testid="support-reply-cancel">
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={sending || body.trim().length < 2}
            className="rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold"
            data-testid="support-reply-send"
          >
            {sending ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</>) : (<><Send className="w-3.5 h-3.5 mr-1.5" /> Send reply</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
